/* Pure layout maths for a day grid. No React, no styling — so it can be tested
 * directly, which matters because this is where the silent bugs live: an event
 * placed at the wrong offset, or two meetings drawn on top of each other with one
 * invisible.
 *
 * Calendar colours are a categorical encoding (which calendar an event belongs
 * to), so the palette below was produced by running the dataviz validator rather
 * than picked by eye. Four slots is the ceiling: adding a fifth hue put magenta
 * and red at ΔE 13.0 on normal vision, under the hard floor of 15. Past four
 * calendars the page facets into side-by-side columns instead of inventing a
 * hue — which is also what Outlook does.
 */

export const HOUR_H = 48;      // px per hour
export const GUTTER_W = 52;    // hour-label column
export const MIN_EVENT_H = 18; // a 15-minute meeting must still be readable
export const DEFAULT_FROM = 8; // always show at least 8 AM …
export const DEFAULT_TO = 18;  // … through 6 PM, expanded to fit outliers

// Validated all-pairs on a light surface. Order is fixed and meaningful:
// assignment is by stable calendar id, never by position, so toggling one
// calendar off must not repaint the others.
export const CALENDAR_COLORS = ['#0069a6', '#b7791f', '#a3357a', '#1a7f4e'];
export const MAX_OVERLAY_CALENDARS = CALENDAR_COLORS.length;

/** Stable fallback colour: keyed on position in the FULL calendar list, not the
 *  visible subset, so hiding one doesn't recolour the rest. */
export function colorForCalendar(calendarId, allCalendarIds) {
  const i = allCalendarIds.indexOf(calendarId);
  return i >= 0 ? CALENDAR_COLORS[i % CALENDAR_COLORS.length] : '#58585b';
}

/* Outlook's own calendar colours.
 *
 * Graph returns a `calendarColor` enum, not a hex — and the enum names are all
 * "light*" because that's how Outlook tints its backgrounds. Rendering those pale
 * values as a border would be almost invisible on white, so each maps to a
 * saturated version of the same hue: recognisably "the green calendar" while
 * still passing contrast against the grid.
 *
 * 'auto' means Outlook picks the colour itself and does not tell us which, so
 * there is nothing to mirror — those fall back to the validated palette.
 */
export const OUTLOOK_COLORS = {
  lightBlue:   '#0069a6',
  lightGreen:  '#1a7f4e',
  lightOrange: '#c2410c',
  lightGray:   '#58585b',
  lightYellow: '#b7791f',
  lightTeal:   '#0f766e',
  lightPink:   '#a3357a',
  lightBrown:  '#8a5a2b',
  lightRed:    '#c0392b',
};

/** Resolve the colour for every calendar, preferring Outlook's own.
 *
 * Two cases fall back to the validated palette rather than mirroring Outlook:
 *
 *  - `auto`, because Graph won't say what Outlook actually chose.
 *  - a colour ALREADY TAKEN by another calendar. Outlook happily lets someone
 *    have three "lightBlue" calendars; on a single overlaid grid that renders as
 *    three identical colours, and telling two meetings apart by colour becomes
 *    impossible. Mirroring is a nicety — distinguishability isn't.
 *
 * Returns a map of calendarId -> hex. Computed over the FULL list so the answer
 * doesn't change when a calendar is toggled off.
 */
export function resolveCalendarColors(calendars) {
  const all = (calendars || []).map((c) => c.id);
  const used = new Set();
  const out = {};
  // First pass: honour Outlook's colour where it's known and unclaimed.
  for (const c of calendars || []) {
    const hex = OUTLOOK_COLORS[c.color];
    if (hex && !used.has(hex)) {
      used.add(hex);
      out[c.id] = hex;
    }
  }
  // Second pass: everything left gets the next validated slot not already in use,
  // so a fallback never collides with a mirrored colour either.
  let slot = 0;
  for (const c of calendars || []) {
    if (out[c.id]) continue;
    let hex = colorForCalendar(c.id, all);
    while (used.has(hex) && slot < CALENDAR_COLORS.length) {
      hex = CALENDAR_COLORS[slot++];
    }
    if (used.has(hex)) hex = '#58585b';   // ran out — neutral, name carries identity
    used.add(hex);
    out[c.id] = hex;
  }
  return out;
}

/** Split events into the three cases a grid has to handle differently. */
export function partitionEvents(events) {
  const allDay = [];
  const timed = [];
  const untimed = [];
  for (const e of events || []) {
    if (e.is_all_day) { allDay.push(e); continue; }
    if (!Number.isFinite(e.start_minutes)) { untimed.push(e); continue; }
    timed.push({
      ...e,
      startMin: e.start_minutes,
      // Guarantee a non-zero span even if end is missing or malformed.
      endMin: Number.isFinite(e.end_minutes) && e.end_minutes > e.start_minutes
        ? e.end_minutes
        : e.start_minutes + 30,
    });
  }
  timed.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  return { allDay, timed, untimed };
}

/** Group timed events into clusters of transitively-overlapping events.
 *
 * Clustering first is what makes widths local: a single 8-way overlap at 9 AM
 * should not narrow an unrelated 4 PM meeting to an eighth of the column, which
 * is what a day-global column count does.
 */
export function clusterEvents(timed) {
  const clusters = [];
  let current = [];
  let clusterEnd = -1;
  for (const e of timed) {
    if (current.length && e.startMin >= clusterEnd) {
      clusters.push(current);
      current = [];
      clusterEnd = -1;
    }
    current.push(e);
    clusterEnd = Math.max(clusterEnd, e.endMin);
  }
  if (current.length) clusters.push(current);
  return clusters;
}

/** Assign each event a column within its own cluster.
 *
 * Greedy: drop each event into the first column whose last event has ended.
 * Without this two meetings at the same time render exactly on top of each other
 * and one is invisible — a silent failure, the worst kind on a calendar.
 */
export function packCluster(cluster) {
  const colEnds = [];
  const placed = cluster.map((e) => {
    let idx = colEnds.findIndex((endsAt) => endsAt <= e.startMin);
    if (idx === -1) { colEnds.push(e.endMin); idx = colEnds.length - 1; }
    else colEnds[idx] = e.endMin;
    return { ...e, col: idx };
  });
  return { placed, colCount: Math.max(1, colEnds.length) };
}

/** Full layout for one day: every timed event with its column index and the
 *  column count OF ITS OWN CLUSTER. */
export function layoutDay(events) {
  const { allDay, timed, untimed } = partitionEvents(events);
  const placed = [];
  for (const cluster of clusterEvents(timed)) {
    const { placed: rows, colCount } = packCluster(cluster);
    for (const r of rows) placed.push({ ...r, colCount });
  }
  return { allDay, placed, untimed };
}

/** Visible hour range: a fixed working-day window, widened to fit outliers so an
 *  early or late meeting is never clipped off the grid. */
export function hourRange(timed) {
  const from = Math.min(DEFAULT_FROM, ...timed.map((e) => Math.floor(e.startMin / 60)));
  const to = Math.max(DEFAULT_TO, ...timed.map((e) => Math.ceil(e.endMin / 60)));
  return { fromHour: Number.isFinite(from) ? from : DEFAULT_FROM, toHour: Number.isFinite(to) ? to : DEFAULT_TO };
}

/** Pixel box for one placed event, given the grid origin. */
export function eventBox(e, originMin) {
  const top = ((e.startMin - originMin) / 60) * HOUR_H;
  const height = Math.max(MIN_EVENT_H, ((e.endMin - e.startMin) / 60) * HOUR_H - 2);
  const widthPct = 100 / e.colCount;
  return { top, height, leftPct: widthPct * e.col, widthPct };
}

export function hourLabel(h) {
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${suffix}`;
}

/** Split attendees into internal and external for the pre-write confirmation.
 *  Mirrors INTERNAL_DOMAINS in api/app/routers/calendar.py. */
export const INTERNAL_DOMAINS = ['prosperitycapitaladvisors.com', 'c2penterprises.com'];

export function attendeeSplit(attendees) {
  const rows = (attendees || []).filter((a) => (a.email || '').trim());
  const external = rows.filter(
    (a) => !INTERNAL_DOMAINS.some((d) => a.email.trim().toLowerCase().endsWith(`@${d}`)),
  );
  return { total: rows.length, external: external.length };
}

/** Parse the attendee text field into `[{email}]`.
 *
 * Kept separate from the input's own state on purpose. The field used to be
 * bound straight to the parsed array, which meant every keystroke round-tripped
 * through parse→join: typing a trailing comma produced an empty segment, the
 * empty segment was dropped, and the re-rendered value came back WITHOUT the
 * comma — so the character vanished as it was typed and a second address could
 * never be started. The raw text is now the state, and parsing happens here,
 * only when the value is actually used.
 */
export function parseAttendees(text) {
  if (Array.isArray(text)) return text;               // already parsed
  return String(text || '')
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((email) => ({ email }));
}

/** Who was added and who was removed, comparing by email.
 *
 * Case-insensitive, because Outlook is inconsistent about the case it returns
 * and a case-only difference would read as "removed X, added X" — which would
 * push an otherwise additions-only edit onto the notify-everyone path.
 */
export function attendeeDiff(before, after) {
  const key = (a) => (a?.email || '').trim().toLowerCase();
  const beforeSet = new Set((before || []).map(key).filter(Boolean));
  const afterSet = new Set((after || []).map(key).filter(Boolean));
  return {
    added: (after || []).filter((a) => key(a) && !beforeSet.has(key(a))),
    removed: (before || []).filter((a) => key(a) && !afterSet.has(key(a))),
  };
}

/** True when the ONLY thing that changed is that people were added.
 *
 * Deliberately strict: any change to time, subject or location — or any removal —
 * disqualifies it, because those genuinely need to reach everyone. Getting this
 * wrong in the permissive direction means a client silently doesn't hear that
 * their meeting moved, which is far worse than an extra email.
 */
export function onlyAddsAttendees(form, diff) {
  const o = form.original;
  if (!o) return false;                       // no snapshot -> assume the worst
  if (!diff.added.length || diff.removed.length) return false;
  return (form.subject || '') === (o.subject || '')
    && (form.location || '') === (o.location || '')
    && form.startInput === o.startInput
    && form.endInput === o.endInput;
}

/* ── Outlook categories (per-meeting colour) ────────────────────────────────
 *
 * Outlook colours an individual meeting by its CATEGORY, and falls back to the
 * calendar colour when there isn't one. This mirrors that precedence, because a
 * page that ignored categories would show a differently-coloured grid from the
 * Outlook the user is looking at two windows over.
 *
 * Graph gives category NAMES on the event and the name→colour mapping in a
 * separate master list, keyed by `preset0`…`preset24`. The preset order is
 * Outlook's, not arbitrary. As with calendar colours these are saturated enough
 * to read as a border on white rather than Outlook's pale fills.
 */
export const CATEGORY_PRESETS = {
  preset0:  '#c0392b', // red
  preset1:  '#c2410c', // orange
  preset2:  '#8a5a2b', // brown
  preset3:  '#b7791f', // yellow
  preset4:  '#1a7f4e', // green
  preset5:  '#0f766e', // teal
  preset6:  '#6b7a2b', // olive
  preset7:  '#0069a6', // blue
  preset8:  '#7a3fa8', // purple
  preset9:  '#a3357a', // cranberry
  preset10: '#5b7c99', // steel
  preset11: '#3d5666', // dark steel
  preset12: '#767676', // grey
  preset13: '#4a4a4a', // dark grey
  preset14: '#1f1f1f', // black
  preset15: '#8f2620', // dark red
  preset16: '#8a3208', // dark orange
  preset17: '#5e3d1c', // dark brown
  preset18: '#7d5313', // dark yellow
  preset19: '#115635', // dark green
  preset20: '#0a504a', // dark teal
  preset21: '#48541e', // dark olive
  preset22: '#004a75', // dark blue
  preset23: '#552b76', // dark purple
  preset24: '#722555', // dark cranberry
};

// The six defaults are named after their own colour, so they can be resolved
// without MailboxSettings.Read. Renamed or custom categories need the master list.
const DEFAULT_CATEGORY_NAMES = {
  red: 'preset0', orange: 'preset1', brown: 'preset2', yellow: 'preset3',
  green: 'preset4', teal: 'preset5', olive: 'preset6', blue: 'preset7',
  purple: 'preset8', cranberry: 'preset9',
};

/** name (lowercased) -> hex, from the mailbox's master category list. */
export function buildCategoryColors(masterCategories) {
  const out = {};
  for (const c of masterCategories || []) {
    const hex = CATEGORY_PRESETS[c.preset];
    if (hex && c.name) out[c.name.trim().toLowerCase()] = hex;
  }
  return out;
}

/** Colour for one event: its category if we can resolve one, else its calendar.
 *
 * Three resolution steps, most to least reliable:
 *   1. the mailbox's master category list (exact, needs MailboxSettings.Read)
 *   2. the category NAME, for the untouched defaults ("Purple Category")
 *   3. the calendar's colour — always available
 *
 * An event can carry several categories; Outlook shows several colour bars but
 * one line on a grid can only take one, so the first resolvable one wins. That
 * matches how Outlook's own compact views behave.
 */
export function colorForEvent(event, categoryColors, calendarColors) {
  for (const raw of event?.categories || []) {
    const name = (raw || '').trim().toLowerCase();
    if (!name) continue;
    if (categoryColors?.[name]) return categoryColors[name];
    const word = name.replace(/\s*category$/, '');
    const preset = DEFAULT_CATEGORY_NAMES[word];
    if (preset) return CATEGORY_PRESETS[preset];
  }
  return calendarColors?.[event?.calendar_id] || '#58585b';
}
